/*
 * Copyright (c) 2025 curryfirm.com
 */

// TODO: Add "fuzzy match" and interactive match confirmation to DumpsterTrack?
// TODO: Support time-of-day haul return or delivery constraint
// TODO: Revamp architecture around selecting cans and picking intent with each.

import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Alert from '@mui/material/Alert';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import AuthForm from './components/Auth/AuthForm.js';
import { useAuth } from './hooks/useAuth.js';

// Redux step constants and actions
import {
    nextStep,
    prevStep,
    setStep,
    setReturnToSummaryAvailable,
    WIZARD_STEPS,
} from './features/workflow/workflowSlice.js';
import ChooseEmptyCan from './components/wizardSteps/ChooseEmptyCan/ChooseEmptyCan.js';
import StartEndLocationChooser from './components/wizardSteps/StartEndLocationChooser/StartEndLocationChooser.js';
import DumpsterSelection from './components/wizardSteps/ChooseDumpsters/ChooseDumpsters.js';
import WizardSummary from './components/wizardSteps/WizardSummary/WizardSummary.js';
import DisplayRoute from './components/wizardSteps/DisplayRoute/DisplayRoute.js';

import * as constants from './constants.js';
import Button from '@mui/material/Button';
import useSaveWizardLocationFromParams from "./hooks/useSaveWizardLocationFromParams.js";
import ConfirmDumpLocations from "./components/wizardSteps/ConfirmDumpLocations/ConfirmDumpLocations.js";
import ChooseRouteType from "./components/wizardSteps/ChooseRouteType/ChooseRouteType.js";

const App = () => {
    const dispatch = useDispatch();
    const step = useSelector(state => state.workflow.step);
    const error = useSelector(state => state.workflow.error);
    const { user, authChecked } = useAuth();
    const [ready, setReady] = useState(false);

    // Save user's current location, passed as parameters on URL to invoke this app.
    // Only saves after authentication.
    useSaveWizardLocationFromParams(user);

    // Optionally: handle user auth and initial data setup here as before
    if (constants.DEBUG && !ready) {
        return (
            <div style={{padding: 32}}>
                <h2>Waiting for Debugger Attachment</h2>
                <Button variant="contained" color="primary" onClick={() => setReady(true)}>Continue</Button>
            </div>
        );
    }


    // Step switcher
    const renderStep = () => {
        switch (step) {
            case WIZARD_STEPS.CHOOSE_ROUTE_TYPE:
                return <ChooseRouteType onContinue={() => dispatch(nextStep())}/>;
            case WIZARD_STEPS.CONFIRM_DUMP_LOCATIONS:
                return <ConfirmDumpLocations onBack={() => dispatch(prevStep())} onContinue={() => dispatch(nextStep())}/>;
            case WIZARD_STEPS.CHOOSE_EMPTY_CAN:
                return <ChooseEmptyCan onBack={() => dispatch(prevStep())} onContinue={() => dispatch(nextStep())}/>;
            case WIZARD_STEPS.START_LOCATION:
                return <StartEndLocationChooser
                    label={(`${constants.START_LOCATION_STEP_LABEL}`)}
                    onBack={() => dispatch(prevStep())}
                    onContinue={() => dispatch(nextStep())}
                    isStart={true}
                />;
            case WIZARD_STEPS.END_LOCATION:
                return <StartEndLocationChooser
                    label={(`${constants.END_LOCATION_STEP_LABEL}`)}
                    onBack={() => dispatch(prevStep())}
                    onContinue={() => dispatch(nextStep())}
                    isStart={false}
                />;
            case WIZARD_STEPS.DUMPSTER_SELECTION:
                return <DumpsterSelection onBack={() => dispatch(prevStep())} onContinue={() => dispatch(nextStep())} />;
            case WIZARD_STEPS.SUMMARY:
                const editHandlers = {
                    editRouteType: () => {
                        dispatch(setReturnToSummaryAvailable(true));
                        dispatch(setStep(WIZARD_STEPS.CHOOSE_ROUTE_TYPE));
                    },
                    editDumps: () => {
                        dispatch(setReturnToSummaryAvailable(true));
                        dispatch(setStep(WIZARD_STEPS.CONFIRM_DUMP_LOCATIONS));
                    },
                    editEmptyCanInventory: () => {
                        dispatch(setReturnToSummaryAvailable(true));
                        dispatch(setStep(WIZARD_STEPS.CHOOSE_EMPTY_CAN));
                    },
                    editStartingLocation: () => {
                        dispatch(setReturnToSummaryAvailable(true));
                        dispatch(setStep(WIZARD_STEPS.START_LOCATION))
                    },
                    editEndingLocation: () => {
                        dispatch(setReturnToSummaryAvailable(true));
                        dispatch(setStep(WIZARD_STEPS.END_LOCATION))
                    },
                    editDumpsterSelection: () => {
                        dispatch(setReturnToSummaryAvailable(true));
                        dispatch(setStep(WIZARD_STEPS.DUMPSTER_SELECTION))
                    },
                };
                return <WizardSummary
                    editHandlers={editHandlers}
                    onBack={() => dispatch(prevStep())}
                    onContinue={() => dispatch(nextStep())}
                />;
            case WIZARD_STEPS.DISPLAY_ROUTE:
                return <DisplayRoute onBack={() => dispatch(prevStep())}/>;

            // return <DisplayRoute
                //     onBack={() => dispatch(prevStep())}
                // />;
            default:
                return <div>Wizard step not found: '{step}'</div>;
        }
    }

    return (
        <Container maxWidth="md" sx={{ marginTop: 4 }}>
            <Paper elevation={3} sx={{ padding: 4 }}>
                { !authChecked ? (
                    <div>Loading...</div>
                ) : !user ? (
                    <AuthForm />
                ) : (
                    <div>
                        {error && <Alert severity="error">{error}</Alert>}
                        {renderStep()}
                    </div>
                )}
            </Paper>
        </Container>
    );
};

export default App;